import { Building2Icon } from "lucide-react";
import type { JSONContent } from "@tiptap/react";

import { CommentComposer } from "./CommentComposer";
import { ExternalComment } from "./ExternalComment";

import { Column, LoadingIndicator, Text } from "@/components";

import { useSheetScope } from "@/contexts/SheetScopeContext";
import { useCreateAddExternalCandidacyComment } from "@/hooks/createAdds/useCreateAddExternalCandidacyComment";
import { useGetExternalCandidacyComments } from "@/hooks/queries/useGetExternalCandidacyComments";
import { mixpanelUtils } from "@/lib/mixpanel/mixpanel";
import { MIXPANEL_SCREENS } from "@/lib/mixpanel/types";
import { tiptap } from "@/lib/tiptap";

const jsonToHtml = tiptap.jsonToHtmlFn();

export const ExternalComments = ({
  commentDraft,
  onCommentDraftChange,
}: {
  commentDraft: JSONContent;
  onCommentDraftChange: (contentJson: JSONContent) => void;
}) => {
  /* Context */
  const { candidacyId, companyName } = useSheetScope();

  /* Database CRUD Operations */
  // Read
  const commentsQuery = useGetExternalCandidacyComments({ candidacyId });
  // Create
  const { isPending, mutate: addComment } =
    useCreateAddExternalCandidacyComment();

  /* Helpers */
   
  const displayCompanyName = companyName.trim() || "the company";

  /* Render */
  const renderFeed = () => {
    if (commentsQuery.isPending) {
      return <LoadingIndicator />;
    }

    if (commentsQuery.isError) {
      return (
        <Text className="text-sm font-normal text-slate-500">
          Couldn&apos;t load messages. Try again.
        </Text>
      );
    }

    const comments = [...commentsQuery.data].sort(
      (a, b) =>
        new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
    );

    if (comments.length === 0) {
      return (
        <Text className="text-sm font-normal text-slate-500">
          No messages yet.
        </Text>
      );
    }

    return (
      <Column className="gap-5">
        {comments.map((comment) => (
          <ExternalComment comment={comment} key={comment.id} />
        ))}
      </Column>
    );
  };

  const render = () => {
    return (
      <Column className="h-full min-h-0 w-full gap-0">
        <Column className="min-h-0 flex-1 overflow-y-auto pb-3">
          {renderFeed()}
        </Column>
        <Column className="shrink-0">
          <CommentComposer
            banner={
              <>
                <Building2Icon className="h-3.5 w-3.5 shrink-0" />
                <Text className="min-w-0 truncate text-xs font-medium text-slate-500">
                  Communicating with {displayCompanyName}
                </Text>
              </>
            }
            draft={commentDraft}
            isPending={isPending}
            onDraftChange={onCommentDraftChange}
            onSend={(contentJson, reset) => {
              mixpanelUtils.trackFormSubmission(
                "Add external comment",
                MIXPANEL_SCREENS.CANDIDATE_SHEET,
              );
              addComment(
                {
                  candidacyId,
                  createExternalCandidacyCommentRequest: {
                    content: jsonToHtml(contentJson),
                  },
                },
                {
                  onSuccess: () => {
                    reset();
                  },
                },
              );
            }}
            placeholder="Write a message..."
          />
        </Column>
      </Column>
    );
  };

  return render();
};
